import { useCallback, useEffect, useState } from "react";
import { Button, Checkbox, Toggle, useToast } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { Modal } from "../../components/Modal/Modal";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./AssignReviewersModal.prefix.css";

const block = cn("assign-reviewers");

// Modal for bulk-assigning reviewers to tasks.
// Task picker supports three modes:
//   all         — every task in the project
//   unreviewed  — tasks with at least one annotation still awaiting review
//   ids         — manager pastes / types a comma-separated ID list
// Reviewer picker lists project members filtered to Reviewer / Manager /
// Admin / Owner roles (the roles the backend will accept).
const SCOPE_MODES = [
  { key: "all", label: "All tasks in this project" },
  { key: "unreviewed", label: "Only tasks with unreviewed annotations" },
  { key: "ids", label: "Specific task IDs" },
];

const REVIEWER_ROLES = new Set(["OW", "AD", "MA", "RE"]);

export const AssignReviewersModal = ({ projectId, onClose, onAssigned }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [mode, setMode] = useState("all");
  const [idsInput, setIdsInput] = useState("");
  const [replace, setReplace] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    api
      .callApi("projectMembers", {
        params: { pk: projectId },
        suppressError: true,
      })
      .then((res) => {
        if (cancelled) return;
        setLoading(false);
        if (!res || res.error) return;
        const rows = Array.isArray(res) ? res : (res.results ?? []);
        // Only users with a role that's allowed to review. Managers
        // and admins can always review; the dedicated Reviewer role
        // is the main audience. Guests / annotators are filtered out.
        const eligible = rows.filter((m) => REVIEWER_ROLES.has(m.role || ""));
        setMembers(eligible);
      });
    return () => {
      cancelled = true;
    };
  }, [api, projectId]);

  const toggleMember = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const submit = useCallback(async () => {
    if (selected.size === 0) {
      toast.show({ message: t("pages.settings.assign_reviewers_modal.pick_at_least_one_reviewer"), type: "error" });
      return;
    }

    let taskIds = [];
    if (mode === "ids") {
      taskIds = idsInput
        .split(/[,\s]+/)
        .map((s) => Number.parseInt(s, 10))
        .filter((n) => Number.isFinite(n));
      if (taskIds.length === 0) {
        toast.show({ message: t("pages.settings.assign_reviewers_modal.enter_at_least_one_task_id"), type: "error" });
        return;
      }
    } else {
      // Scope modes that pick tasks server-side — we fetch the id
      // list first (capped to 10k to keep the POST body reasonable;
      // operators hitting that limit should script it). This keeps
      // the assign endpoint free of scope-mode logic — it just
      // takes explicit task_ids.
      const params = { page: 1, page_size: 10000, include: "id" };
      if (mode === "unreviewed") params.filter = "unreviewed";
      const res = await api.callApi("tasks", {
        params: { ...params },
        suppressError: true,
      });
      const rows = res?.tasks ?? res?.results ?? [];
      taskIds = rows.map((r) => r.id).filter(Boolean);
      if (taskIds.length === 0) {
        toast.show({
          message: t("pages.settings.assign_reviewers_modal.no_tasks_match_the_chosen_scope"),
          type: "error",
        });
        return;
      }
    }

    setSubmitting(true);
    const res = await api.callApi("projectAssignReviewers", {
      params: { pk: projectId },
      body: {
        task_ids: taskIds,
        reviewer_ids: [...selected],
        mode: replace ? "replace" : "add",
      },
      suppressError: true,
    });
    setSubmitting(false);
    if (!res || res.error) {
      toast.show({ message: res?.error ?? "Could not assign reviewers", type: "error" });
      return;
    }
    toast.show({
      message: `Assigned ${res.reviewer_count} reviewer${res.reviewer_count === 1 ? "" : "s"} to ${res.task_count} task${res.task_count === 1 ? "" : "s"}.`,
    });
    onAssigned?.();
    onClose?.();
  }, [api, projectId, mode, idsInput, selected, replace, toast, onAssigned, onClose]);

  return (
    <Modal visible onHide={onClose} style={{ width: 620 }}>
      <Modal.Header divided>
        <div className={cn("modal").elem("title").toClassName()}>Assign reviewers</div>
      </Modal.Header>
      <Modal.Body>
        <div className={block.toClassName()}>
          <section className={block.elem("section").toClassName()}>
            <h3 className={block.elem("section-title").toClassName()}>
              {t("pages.settings.assign_reviewers_modal.pick_reviewers")}
            </h3>
            {loading ? (
              <div className={block.elem("loading").toClassName()}>
                {t("pages.settings.assign_reviewers_modal.loading_members")}
              </div>
            ) : members.length === 0 ? (
              <div className={block.elem("empty").toClassName()}>
                No eligible reviewers on this project. Add members with the Reviewer role first.
              </div>
            ) : (
              <ul className={block.elem("list").toClassName()}>
                {members.map((m) => {
                  const name = [m.user_first_name, m.user_last_name].filter(Boolean).join(" ") || m.user_email;
                  return (
                    <li key={m.id} className={block.elem("row").toClassName()}>
                      <Checkbox
                        checked={selected.has(m.user)}
                        onChange={() => toggleMember(m.user)}
                        ariaLabel={`Toggle ${m.user_email}`}
                      />
                      <div className={block.elem("name").toClassName()}>{name}</div>
                      <div className={block.elem("email").toClassName()}>{m.user_email}</div>
                      <div className={block.elem("role").toClassName()}>{m.role}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className={block.elem("section").toClassName()}>
            <h3 className={block.elem("section-title").toClassName()}>
              {t("pages.settings.assign_reviewers_modal.scope")}
            </h3>
            <div className={block.elem("modes").toClassName()}>
              {SCOPE_MODES.map((m) => (
                <label key={m.key} className={block.elem("mode").toClassName()}>
                  <input
                    type="radio"
                    name="scope"
                    value={m.key}
                    checked={mode === m.key}
                    onChange={() => setMode(m.key)}
                  />
                  <span>{m.label}</span>
                </label>
              ))}
            </div>
            {mode === "ids" && (
              <textarea
                className={block.elem("ids").toClassName()}
                placeholder={t("pages.settings.assign_reviewers_modal.paste_task_ids_comma_or_spaceseparated_e")}
                rows={3}
                value={idsInput}
                onChange={(e) => setIdsInput(e.target.value)}
              />
            )}
          </section>

          <section className={block.elem("section").toClassName()}>
            <Toggle
              label={t("pages.settings.assign_reviewers_modal.replace_existing_reviewers")}
              description={t("pages.settings.assign_reviewers_modal.when_on_the_picked_reviewers_replace_wha")}
              checked={replace}
              onChange={(e) => setReplace(e.target.checked)}
            />
          </section>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="neutral" look="outlined" size="small" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" size="small" onClick={submit} disabled={submitting || selected.size === 0}>
          {submitting ? "Assigning…" : `Assign${selected.size > 0 ? ` ${selected.size}` : ""}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
